import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Snackbar
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { listUsers, deleteUser, User } from '../../api/admin';
import UserCreateDialog from './UserCreateDialog';
import UserEditDialog from './UserEditDialog';
import CreditsDialog from './CreditsDialog';
import PasswordResetDialog from './PasswordResetDialog';

const UserManagement: React.FC = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listUsers(true);
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    setCreateDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleManageCredits = (user: User) => {
    setSelectedUser(user);
    setCreditsDialogOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setPasswordDialogOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Czy na pewno chcesz TRWALE usunąć użytkownika "${user.username}"?`)) {
      return;
    }

    try {
      await deleteUser(user.username, true); // hard delete — permanent removal
      setSnackbar({ open: true, message: `Użytkownik "${user.username}" został usunięty.`, severity: 'success' });
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setSnackbar({ open: true, message: `Nie udało się usunąć użytkownika "${user.username}".`, severity: 'error' });
    }
  };

  const handleDialogClose = (refresh: boolean = false) => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setCreditsDialogOpen(false);
    setPasswordDialogOpen(false);
    setSelectedUser(null);

    if (refresh) {
      fetchUsers();
    }
  };

  // Filter users by search query
  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: t('admin.users.columns.username'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'email',
      headerName: t('admin.users.columns.email'),
      flex: 1.5,
      minWidth: 200
    },
    {
      field: 'role',
      headerName: t('admin.users.columns.role'),
      width: 120,
      renderCell: (params) => {
        const color = params.value === 'admin' ? 'error' : params.value === 'b2b-user' ? 'warning' : 'default';
        return <Chip label={params.value} color={color} size="small" />;
      }
    },
    {
      field: 'credits',
      headerName: t('admin.users.columns.credits'),
      width: 120,
      renderCell: (params) => {
        const value = params.value === 'unlimited' || params.row.credits === 'unlimited' ? '∞' : params.value;
        return <Box sx={{ fontWeight: 600 }}>{value}</Box>;
      }
    },
    {
      field: 'credits_used',
      headerName: t('admin.users.columns.used'),
      width: 100
    },
    {
      field: 'status',
      headerName: t('admin.users.columns.status'),
      width: 100,
      renderCell: (params) => {
        const color = params.value === 'active' ? 'success' : 'default';
        return <Chip label={params.value} color={color} size="small" />;
      }
    },
    {
      field: 'auth_method',
      headerName: t('admin.users.columns.authMethod'),
      width: 100,
      renderCell: (params) => {
        const color = params.value === 'google' ? 'info' : 'default';
        return <Chip label={params.value} color={color} size="small" />;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('admin.users.columns.actions'),
      width: 150,
      getActions: (params) => {
        const user = params.row as User;
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={t('admin.users.actions.edit')}
            onClick={() => handleEditUser(user)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<CreditIcon />}
            label={t('admin.users.actions.credits')}
            onClick={() => handleManageCredits(user)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<LockIcon />}
            label={t('admin.users.actions.password')}
            onClick={() => handleResetPassword(user)}
            disabled={user.auth_method === 'google'}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={t('admin.users.actions.delete')}
            onClick={() => handleDeleteUser(user)}
            showInMenu
          />
        ];
      }
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          {t('admin.users.addUser')}
        </Button>

        <TextField
          size="small"
          placeholder={t('admin.users.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
        />
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row.username}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          density="comfortable"
        />
      </Box>

      {/* Dialogs */}
      <UserCreateDialog
        open={createDialogOpen}
        onClose={handleDialogClose}
      />

      <UserEditDialog
        open={editDialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
      />

      <CreditsDialog
        open={creditsDialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
      />

      <PasswordResetDialog
        open={passwordDialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
